import { Theme } from '@mui/material';
import { Address } from 'src/graphql';
import { SubscriptionPhase } from 'src/graphql/entities/address-subscription/types';
import { ServiceLevelOption } from 'src/graphql/entities/service-level';
import { SubscriptionStatus } from 'src/graphql/entities/stripe-subscription-details';
import { formatAmountCents } from 'src/utils/number';
import { MembershipInformationFormValues } from './use-membership-info-form';

function didChange(
  values: Partial<MembershipInformationFormValues>,
  address: Partial<Address>,
  key: keyof MembershipInformationFormValues,
): boolean {
  if (!(key in values)) return false;
  if (key === 'membershipSalesNotes') return values[key] !== address[key];
  return values[key]?.id !== address[key];
}

export function getMembershipInfoChanges(
  values: Partial<MembershipInformationFormValues>,
  address: Partial<Address>,
  serviceLevelOptions: ServiceLevelOption[],
): Record<string, unknown> | null {
  const changes: Record<string, unknown> = {};

  if ('platformSlaId' in values) {
    const matched = serviceLevelOptions.find((opt) => opt.id === values.platformSlaId?.id);
    const newPlatformSlaId = matched?.platformSlaId ?? null;
    if (newPlatformSlaId !== (address.platformSlaId ?? null)) {
      changes.platformSlaId = newPlatformSlaId;
      changes.clientActivatedMembership = matched?.label ?? values.platformSlaId?.id;
    }
  }

  if (didChange(values, address, 'tosAccepted')) {
    changes.tosAccepted = values.tosAccepted?.id;
  }

  if (didChange(values, address, 'membershipSalesNotes')) {
    changes.membershipSalesNotes = values.membershipSalesNotes;
  }

  return Object.keys(changes).length > 0 ? changes : null;
}

export function formatDateToLocalString(params: {
  date?: string;
  options: Intl.DateTimeFormatOptions;
}) {
  const date = params.date ? new Date(params.date) : new Date();
  return date.toLocaleString('en-US', params.options);
}

export function addZeroTime(dateStr: string): string {
  return `${dateStr}T00:00:00`;
}

export const OPTIONS_LOCALE = {
  month: 'short',
  day: '2-digit',
  year: 'numeric',
} as const;

export function getSubscriptionStatusColor(
  subscriptionStatus: SubscriptionStatus,
  theme: Theme,
) {
  switch (subscriptionStatus) {
    case SubscriptionStatus.Active:
      return theme.palette.custom.OV.sys.success;

    case SubscriptionStatus['Past Due']:
    case SubscriptionStatus.Unpaid:
    case SubscriptionStatus.Canceled:
    case SubscriptionStatus.Incomplete:
    case SubscriptionStatus['Incomplete Expired']:
      return theme.palette.custom.OV.sys.error;

    case SubscriptionStatus.Trialing:
      return theme.palette.info.main;

    default:
      return theme.palette.custom.OV.sys.outline;
  }
}

export enum SubscriptionSchedulePhaseType {
  trial = 'Trial',
  psm = 'Pre-Sold Membership',
  active = 'Active',
  paused = 'Paused',
}

export function getAmount(amount?: number | null, notFoundMessage = 'Not Found') {
  return formatAmountCents(amount, notFoundMessage);
}

export function isSubscriptionInPsmPhase(address?: Partial<Address>): boolean {
  const activeSub = address?.addressSubscriptions?.find((sub) => sub.status !== 'canceled');
  return activeSub?.phase === SubscriptionPhase.Psm;
}

export function isCPPExpired(expDate: string) {
  if (!expDate) return false;
  const date = new Date(expDate);
  const today = new Date();
  return date < today;
}

export function getComplimentaryPeriodActionLabel(expDate?: string) {
  const ADD_LABEL = 'Add complimentary membership';
  const EDIT_LABEL = 'Update complimentary membership';

  if (!expDate) {
    return ADD_LABEL;
  }

  return isCPPExpired(expDate) ? ADD_LABEL : EDIT_LABEL;
}
